type FlagName = 'ENABLE_LEGACY';

const STORAGE_KEY = 'hivecore.flags';
const envDefault = (import.meta.env?.VITE_ENABLE_LEGACY ?? 'true') !== 'false';
const defaultFlags: Record<FlagName, boolean> = {
  ENABLE_LEGACY: envDefault,
};

function load(): Record<FlagName, boolean> {
  if (typeof window === 'undefined') {
    return { ...defaultFlags };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...defaultFlags };
    }
    const parsed = JSON.parse(raw);
    return { ...defaultFlags, ...parsed };
  } catch (error) {
    console.warn('[flags] Failed to load flags', error);
    return { ...defaultFlags };
  }
}

type FlagSnapshot = Readonly<Record<FlagName, boolean>>;

function createSnapshot(flags: Record<FlagName, boolean>): FlagSnapshot {
  return Object.freeze({ ...flags });
}

let _flags: FlagSnapshot = createSnapshot(load());

function save() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(_flags));
  } catch (error) {
    console.warn('[flags] Failed to persist flags', error);
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();

export function getFlags(): FlagSnapshot {
  return _flags;
}

export const FLAGS = new Proxy(_flags, {
  get(_, prop: string) {
    return (_flags as any)[prop];
  }
}) as Record<FlagName, boolean>;

export function onFlagsChange(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setFlag(name: FlagName, value: boolean) {
  if (_flags[name] === value) {
    return;
  }

  _flags = createSnapshot({ ..._flags, [name]: value });
  save();
  listeners.forEach(listener => listener());
}

declare global {
  interface Window {
    Flags?: {
      FLAGS: Record<FlagName, boolean>;
      setFlag: typeof setFlag;
      onFlagsChange: typeof onFlagsChange;
    };
  }
}

if (typeof window !== 'undefined') {
  window.Flags = { FLAGS, setFlag, onFlagsChange };
  window.addEventListener('storage', event => {
    if (event.key === STORAGE_KEY) {
      _flags = createSnapshot(load());
      listeners.forEach(listener => listener());
    }
  });
}

